import { useState, useMemo, useEffect, useRef } from 'react';
import { isEqual } from 'lodash';
import { Store } from 'components/common/types/Store.types';
import { RouteFrameCode } from 'components/common/types/Deal.types';
import { FileList } from 'components/common/types';
import usePrevious from 'customHooks/usePrevious';
import { permissionOptions } from '../../consts';

export const useProductFrameListState = (
  productFrameList: Store['productManagement']['newProduct']['frameList'],
): {
  selectedPermission: { code: string; name: string };
  setSelectedPermission: React.Dispatch<React.SetStateAction<{ code: string; name: string }>>;
  selectedRouteFrameCodes: RouteFrameCode[];
  setSelectedRouteFrameCodes: React.Dispatch<React.SetStateAction<RouteFrameCode[]>>;
  uploadedListOfFiles: FileList[];
  setUploadedListOfFiles: React.Dispatch<React.SetStateAction<FileList[]>>;
  initialRouteFrameCodes: RouteFrameCode[];
  initialListFiles: FileList[];
  defaultSelectedPermission: { code: string; name: string };
} => {
  const defaultSelectedPermission = useMemo(
    () => permissionOptions.find(({ code }) => code === productFrameList?.selectionPermission) ?? permissionOptions[0],
    [productFrameList?.selectionPermission],
  );

  const [selectedPermission, setSelectedPermission] = useState(defaultSelectedPermission);
  const previousDefaultPermissionCodeRef = useRef<string>(defaultSelectedPermission.code);

  const initialRouteFrameCodes = useMemo(
    () => productFrameList?.value?.routeFrameCodes ?? [],
    [productFrameList?.value?.routeFrameCodes],
  );
  const initialListFiles = useMemo<FileList[]>(
    () => productFrameList?.value?.listFiles ?? [],
    [productFrameList?.value?.listFiles],
  );

  const [selectedRouteFrameCodes, setSelectedRouteFrameCodes] = useState<RouteFrameCode[]>(initialRouteFrameCodes);
  const [uploadedListOfFiles, setUploadedListOfFiles] = useState<FileList[]>(initialListFiles);

  const prevInitialRouteFrameCodes = usePrevious(initialRouteFrameCodes);
  const prevInitialListFiles = usePrevious(initialListFiles);

  useEffect(() => {
    if (
      prevInitialRouteFrameCodes !== undefined &&
      !isEqual(prevInitialRouteFrameCodes, initialRouteFrameCodes) &&
      !isEqual(initialRouteFrameCodes, selectedRouteFrameCodes)
    ) {
      setSelectedRouteFrameCodes(initialRouteFrameCodes);
    }
  }, [initialRouteFrameCodes, selectedRouteFrameCodes, prevInitialRouteFrameCodes]);

  useEffect(() => {
    if (
      prevInitialListFiles !== undefined &&
      !isEqual(prevInitialListFiles, initialListFiles) &&
      !isEqual(initialListFiles, uploadedListOfFiles)
    ) {
      setUploadedListOfFiles(initialListFiles);
    }
  }, [initialListFiles, uploadedListOfFiles, prevInitialListFiles]);

  useEffect(() => {
    if (previousDefaultPermissionCodeRef.current !== defaultSelectedPermission.code) {
      previousDefaultPermissionCodeRef.current = defaultSelectedPermission.code;
      setSelectedPermission(defaultSelectedPermission);
    }
  }, [defaultSelectedPermission]);

  return {
    selectedPermission,
    setSelectedPermission,
    selectedRouteFrameCodes,
    setSelectedRouteFrameCodes,
    uploadedListOfFiles,
    setUploadedListOfFiles,
    initialRouteFrameCodes,
    initialListFiles,
    defaultSelectedPermission,
  };
};
